import { DOCUMENT, Injectable, inject } from '@angular/core';
import { NavigationStart, Router, type ViewTransitionInfo } from '@angular/router';
import { filter } from 'rxjs';
import { ViewportService } from '../viewport/viewport.service';

const MOBILE_QUERY = '(max-width: 767px)';

/**
 * Drives the directional page transitions on the mobile shell.
 *
 * The router (see {@link withViewTransitions} in `app.config.ts`) wraps every
 * navigation in `document.startViewTransition`. This service decides the
 * *direction* of that transition — drilling into a deeper screen slides forward
 * (new from the right), going back slides the other way — by tracking a small
 * URL stack and honouring browser/gesture back (`popstate`).
 *
 * The direction is exposed to CSS via `data-vt="forward" | "back"` on `<html>`;
 * the keyframes that read it live in `styles/page-transitions.css`. Re-tapping
 * the current screen clears the attribute so it gets the neutral fade instead.
 */
@Injectable({ providedIn: 'root' })
export class PageTransitionService {
  private readonly router = inject(Router);
  private readonly viewport = inject(ViewportService);
  private readonly doc = inject(DOCUMENT);

  /** Visited URLs (path only), most-recent last. Bounded so it can't grow forever. */
  private readonly stack: string[] = [];

  init(): void {
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => this.onNavigationStart(e));
  }

  private onNavigationStart(e: NavigationStart): void {
    const root = this.doc.documentElement;
    // Transitions are a mobile-shell affordance only; leave desktop untouched.
    if (!this.viewport.isMobile()) {
      delete root.dataset['vt'];
      return;
    }

    const url = e.url.split(/[?#]/)[0];
    const idx = this.stack.lastIndexOf(url);

    // Re-tapping the screen we're already on (e.g. tab re-tap with
    // onSameUrlNavigation:'reload') has no direction → neutral fade.
    if (idx >= 0 && idx === this.stack.length - 1) {
      delete root.dataset['vt'];
      return;
    }

    const back = e.navigationTrigger === 'popstate' || idx >= 0;
    if (back) {
      // Unwind the stack to the screen we're returning to.
      if (idx >= 0) this.stack.length = idx + 1;
    } else {
      this.stack.push(url);
      if (this.stack.length > 50) this.stack.shift();
    }
    root.dataset['vt'] = back ? 'back' : 'forward';
  }
}

/**
 * `onViewTransitionCreated` hook for the router. Keeps the view transition
 * scoped to in-app mobile navigation: anywhere else (desktop, or mobile auth /
 * marketing pages that render the desktop shell rather than `.sd-screen`) the
 * transition is skipped so behaviour is unchanged. The `data-vt` direction is
 * cleared once the animation settles.
 */
export function mobileShellViewTransition(info: ViewTransitionInfo): void {
  const inMobileShell =
    typeof window !== 'undefined' &&
    window.matchMedia?.(MOBILE_QUERY).matches &&
    !!document.querySelector('.sd-screen');

  if (!inMobileShell) {
    info.transition.skipTransition();
    return;
  }

  info.transition.finished.finally(() => {
    delete document.documentElement.dataset['vt'];
  });
}
